import {
    Box,
    Button,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Typography,
} from '@mui/material';
import PhosphorIcon from './PhosphorIcon';
import { adminTokens } from './adminTokens';
import React, { ReactNode, useState } from 'react';
import { keyframes } from '@emotion/react';
import logo from 'src/assets/images/hyzebra.png';

export { adminTokens } from './adminTokens';

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

/** Refresh/loading icon — shared between refresh buttons and inline loading indicators so both animate identically. */
export const SpinIcon = ({ spinning, size = 14, color = adminTokens.gray500 }: { spinning: boolean; size?: number; color?: string }) => (
    <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0, animation: spinning ? `${spin} 0.8s linear infinite` : 'none' }}>
        <PhosphorIcon name="arrow-clockwise" size={size} color={color} />
    </Box>
);

export const WizardSteps = ({ steps, activeStep }: { steps: string[]; activeStep: number }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2, borderBottom: adminTokens.borderMd }}>
        {steps.map((label, i) => {
            const state = i < activeStep ? 'done' : i === activeStep ? 'current' : 'upcoming';
            return (
                <React.Fragment key={label}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                        <Box
                            sx={{
                                width: 26,
                                height: 26,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: adminTokens.textXs,
                                fontWeight: 700,
                                flexShrink: 0,
                                bgcolor: state === 'current' ? adminTokens.navy900 : state === 'done' ? adminTokens.green100 : adminTokens.gray100,
                                color: state === 'current' ? '#fff' : state === 'done' ? adminTokens.green700 : adminTokens.gray500,
                            }}
                        >
                            {state === 'done' ? <PhosphorIcon name="check" weight="bold" size={13} color={adminTokens.green700} /> : i + 1}
                        </Box>
                        <Typography
                            sx={{
                                fontSize: adminTokens.textSm,
                                fontWeight: state === 'upcoming' ? 500 : 600,
                                color: state === 'upcoming' ? adminTokens.gray500 : adminTokens.navy800,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {label}
                        </Typography>
                    </Box>
                    {i < steps.length - 1 && (
                        <Box
                            sx={{
                                flex: 1,
                                height: '1.5px',
                                mx: 1.5,
                                minWidth: 16,
                                bgcolor: i < activeStep ? adminTokens.green600 : adminTokens.gray200,
                            }}
                        />
                    )}
                </React.Fragment>
            );
        })}
    </Box>
);

export const EditorNavItem = ({ icon, label, active, onClick, trailing, buttonRef }: {
    icon: string;
    label: ReactNode;
    active?: boolean;
    onClick?: () => void;
    trailing?: ReactNode;
    buttonRef?: any;
}) => (
    <Box
        ref={buttonRef}
        component="button"
        type="button"
        onClick={onClick}
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            height: 40,
            px: 1.5,
            width: '100%',
            border: 'none',
            borderRadius: adminTokens.radiusMd,
            cursor: 'pointer',
            textAlign: 'left',
            fontFamily: adminTokens.fontSans,
            fontSize: adminTokens.textBase,
            bgcolor: active ? adminTokens.gray100 : 'transparent',
            color: active ? adminTokens.navy800 : adminTokens.gray600,
            fontWeight: active ? 600 : 500,
            transition: 'background .1s ease, color .1s ease',
            '&:hover': { bgcolor: adminTokens.gray100, color: adminTokens.navy800 },
        }}
    >
        <PhosphorIcon name={icon} size={16} color={active ? adminTokens.navy800 : adminTokens.gray500} />
        <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</Box>
        {trailing}
    </Box>
);

/** Sidebar "back" row (design system section 08): 40px, ink 600, gray-100 hover. */
export const EditorNavBack = ({ label, onClick }: { label: string; onClick: () => void }) => (
    <Box
        component="button"
        type="button"
        onClick={onClick}
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            height: 40,
            px: 1.5,
            width: '100%',
            border: 'none',
            background: 'none',
            borderRadius: adminTokens.radiusMd,
            cursor: 'pointer',
            textAlign: 'left',
            fontFamily: adminTokens.fontSans,
            fontSize: adminTokens.textBase,
            fontWeight: 600,
            color: adminTokens.navy800,
            '&:hover': { bgcolor: adminTokens.gray100 },
        }}
    >
        <PhosphorIcon name="arrow-left" size={16} color={adminTokens.navy800} />
        {label}
    </Box>
);

export const BackButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
    <Button
        onClick={onClick}
        sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: adminTokens.textBase,
            height: adminTokens.controlHmd,
            color: adminTokens.navy800,
            borderRadius: adminTokens.radiusMd,
            px: 1.5,
            minWidth: 'auto',
            boxShadow: 'none',
            overflow: 'hidden',
            '&:hover': { bgcolor: adminTokens.gray100 },
            '&:hover .nav-back-label': {
                maxWidth: 140,
                opacity: 1,
                ml: 1,
            },
            '&:hover .nav-back-icon': {
                transform: 'translateX(-2px)',
            },
        }}
    >
        <Box
            className="nav-back-icon"
            sx={{
                display: 'inline-flex',
                flexShrink: 0,
                transition: 'transform .2s ease',
            }}
        >
            <PhosphorIcon name="arrow-left" size={16} color={adminTokens.navy800} />
        </Box>
        <Box
            component="span"
            className="nav-back-label"
            sx={{
                display: 'inline-block',
                maxWidth: 0,
                opacity: 0,
                ml: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                transition: 'max-width .25s ease, opacity .2s ease .05s, margin-left .25s ease',
            }}
        >
            {label}
        </Box>
    </Button>
);

export const EditorBar = ({
    title,
    onBack,
    action,
}: {
    title: string;
    onBack: () => void;
    action?: ReactNode;
}) => (
    <Box
        sx={{
            position: 'sticky',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            height: adminTokens.topbarH,
            px: 2,
            bgcolor: adminTokens.surface,
            borderBottom: adminTokens.borderMd,
            top: 0,
            zIndex: 30,
        }}
    >
        <Box
            component="img"
            src={logo}
            alt="hyzebra"
            sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                height: 19,
                pointerEvents: 'none',
            }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, zIndex: 1 }}>
            <BackButton label={title} onClick={onBack} />
        </Box>

        <Box sx={{ zIndex: 1 }}>{action}</Box>
    </Box>
);

export const bodyTextSx = { lineHeight: 1.55, '& .MuiTypography-root': { lineHeight: 'inherit' } };

export const primaryDisabledSx = { '&.Mui-disabled': { opacity: 0.45, bgcolor: adminTokens.ink, color: '#fff' } };

const buttonBase = {
    textTransform: 'none' as const,
    fontWeight: 600,
    fontSize: adminTokens.textBase,
    fontFamily: adminTokens.fontSans,
    borderRadius: adminTokens.radiusMd,
};

export const fieldInputSx = {
    '& .MuiOutlinedInput-root': {
        height: adminTokens.controlHlg,
        fontSize: adminTokens.textBase,
        borderRadius: adminTokens.radiusMd,
        '& fieldset': { borderColor: adminTokens.gray300 },
        '&:hover fieldset': { borderColor: adminTokens.gray300 },
        '&.Mui-focused fieldset': { borderColor: adminTokens.ink, borderWidth: '1px' },
        '&.Mui-disabled fieldset': { borderColor: adminTokens.gray200 },
    },
    '& .MuiOutlinedInput-input': { padding: '0 12px', color: adminTokens.navy800 },
    '& .MuiOutlinedInput-input.Mui-disabled': { WebkitTextFillColor: adminTokens.gray500 },
};
export const multilineFieldInputSx = {
    ...fieldInputSx,
    '& .MuiOutlinedInput-root': { ...fieldInputSx['& .MuiOutlinedInput-root'], height: 'auto', minHeight: 76, alignItems: 'flex-start' },
    '& .MuiOutlinedInput-input': { padding: '10px 12px', color: adminTokens.navy800 },
};

export const PrimaryButton = ({ children, sx = {}, ...rest }: any) => (
    <Button
        variant="contained"
        disableElevation
        sx={{
            ...buttonBase,
            height: adminTokens.controlHmd,
            minWidth: 100,
            px: 2,
            gap: 0.75,
            bgcolor: adminTokens.ink,
            '&:hover': { bgcolor: adminTokens.inkHover },
            '&:focus-visible': {
                outline: 'none',
                boxShadow: `0 0 0 3px ${adminTokens.inkFocus}`,
            },
            '&.Mui-disabled': { opacity: 0.45 },
            ...sx,
        }}
        {...rest}
    >
        {children}
    </Button>
);

export const SecondaryButton = ({ children, sx = {}, fullWidth, ...rest }: any) => (
    <Button
        variant="outlined"
        disableElevation
        fullWidth={fullWidth}
        sx={{
            ...buttonBase,
            height: adminTokens.controlHmd,
            px: 2,
            bgcolor: adminTokens.surface,
            color: adminTokens.navy800,
            borderColor: adminTokens.gray300,
            gap: 0.75,
            '&:hover': {
                bgcolor: adminTokens.gray50,
                borderColor: adminTokens.gray400,
            },
            '&:focus-visible': {
                outline: 'none',
                boxShadow: `0 0 0 3px ${adminTokens.inkFocus}`,
            },
            '&.Mui-disabled': { opacity: 0.45 },
            ...sx,
        }}
        {...rest}
    >
        {children}
    </Button>
);

export const GhostButton = ({ children, sx = {}, ...rest }: any) => (
    <Button
        disableElevation
        sx={{
            ...buttonBase,
            height: adminTokens.controlHmd,
            px: 2,
            gap: 0.75,
            color: adminTokens.gray600,
            bgcolor: 'transparent',
            boxShadow: 'none',
            '&:hover': {
                bgcolor: adminTokens.gray100,
                color: adminTokens.navy800,
                boxShadow: 'none',
            },
            '&:focus-visible': {
                outline: 'none',
                boxShadow: `0 0 0 3px ${adminTokens.inkFocus}`,
            },
            ...sx,
        }}
        {...rest}
    >
        {children}
    </Button>
);

export const AiButton = ({ children, sx = {}, ...rest }: any) => (
    <Button
        variant="contained"
        disableElevation
        sx={{
            ...buttonBase,
            height: adminTokens.controlHmd,
            px: 2,
            bgcolor: adminTokens.ai600,
            color: '#fff',
            gap: 1,
            '&:hover': { bgcolor: adminTokens.ai700 },
            '&:focus-visible': {
                outline: 'none',
                boxShadow: `0 0 0 3px ${adminTokens.aiFocus}`,
            },
            '&.Mui-disabled': { opacity: 0.45, bgcolor: adminTokens.ai600, color: '#fff' },
            ...sx,
        }}
        {...rest}
    >
        {children}
    </Button>
);

export const AiOutlineButton = ({ children, sx = {}, ...rest }: any) => (
    <Button
        variant="contained"
        disableElevation
        sx={{
            ...buttonBase,
            height: adminTokens.controlHmd,
            px: 2,
            bgcolor: adminTokens.ai100,
            color: adminTokens.ai700,
            gap: 1,
            boxShadow: 'none',
            '&:hover': { bgcolor: '#E6E0F9', boxShadow: 'none' },
            '&:focus-visible': {
                outline: 'none',
                boxShadow: `0 0 0 3px ${adminTokens.aiFocus}`,
            },
            ...sx,
        }}
        {...rest}
    >
        {children}
    </Button>
);

export const IconGhostButton = React.forwardRef(({ children, sx = {}, ...rest }: any, ref) => (
    <IconButton
        ref={ref}
        sx={{
            width: adminTokens.controlHmd,
            height: adminTokens.controlHmd,
            borderRadius: adminTokens.radiusMd,
            color: adminTokens.gray500,
            '&:hover': { bgcolor: adminTokens.gray100, color: adminTokens.navy800 },
            ...sx,
        }}
        {...rest}
    >
        {children}
    </IconButton>
));
IconGhostButton.displayName = 'IconGhostButton';

/** Common removable pill chip — gray100 background, name + optional × remove button. */
export const InputChip = ({
    label,
    onDelete,
    onClick,
    sx = {},
    ...rest
}: {
    label: ReactNode;
    onDelete?: (event?: any) => void;
    onClick?: () => void;
    sx?: object;
    [key: string]: any;
}) => (
    <Box
        onClick={onClick}
        {...rest}
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            height: 28,
            padding: '0 10px',
            maxWidth: '100%',
            background: adminTokens.gray100,
            borderRadius: adminTokens.radius,
            fontFamily: adminTokens.fontSans,
            fontSize: adminTokens.textSm,
            fontWeight: 500,
            color: adminTokens.navy800,
            cursor: onClick ? 'pointer' : 'default',
            '&:hover': onClick ? { background: adminTokens.gray200 } : {},
            ...sx,
        }}
    >
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {label}
        </Box>
        {onDelete && (
            <Box
                component="button"
                type="button"
                onClick={(e: any) => { e.stopPropagation(); onDelete(e); }}
                aria-label={typeof label === 'string' ? `Remove ${label}` : 'Remove'}
                sx={{
                    background: 'none', border: 'none', color: adminTokens.gray500, cursor: 'pointer',
                    display: 'flex', flexShrink: 0, padding: 0,
                    '&:hover': { color: adminTokens.navy800 },
                }}
            >
                <PhosphorIcon name="x" size={10} color="currentColor" />
            </Box>
        )}
    </Box>
);

export const NavGroupLabel = ({ children, sx = {} }: { children: ReactNode; sx?: object }) => (
    <Typography
        sx={{
            fontSize: adminTokens.textXs,
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: adminTokens.gray500,
            px: 1.5,
            pt: 2,
            pb: 1,
            fontFamily: adminTokens.fontSans,
            ...sx,
        }}
    >
        {children}
    </Typography>
);

/** Card shell — sidebar & main panel */
export const DsPanel = ({ children, sx = {} }: { children: ReactNode; sx?: object }) => (
    <Box
        sx={{
            bgcolor: adminTokens.surface,
            border: adminTokens.borderMd,
            borderRadius: adminTokens.radiusLg,
            boxShadow: adminTokens.shadowCard,
            ...sx,
        }}
    >
        {children}
    </Box>
);

export const FeedShell = ({ children }: { children: ReactNode }) => (
    <Box
        sx={{
            display: 'flex',
            gap: 2,
            p: 2,
            alignItems: 'flex-start',
            bgcolor: adminTokens.bg,
            fontFamily: adminTokens.fontSans,
            fontSize: adminTokens.textMd,
            color: adminTokens.navy800,
            lineHeight: 1.55,
        }}
    >
        {children}
    </Box>
);

/** Sidebar nav row with kebab menu */
export const FeedNavItem = ({
    label,
    active,
    onClick,
    onDuplicate,
    onDelete,
}: {
    label: string;
    active: boolean;
    onClick: () => void;
    onDuplicate?: () => void;
    onDelete: () => void;
}) => {
    const [anchor, setAnchor] = useState<null | HTMLElement>(null);

    return (
        <Box
            onClick={onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: 40,
                pl: 1.5,
                pr: 0.5,
                borderRadius: adminTokens.radiusMd,
                cursor: 'pointer',
                bgcolor: active ? adminTokens.gray100 : 'transparent',
                color: active ? adminTokens.navy800 : adminTokens.gray600,
                fontWeight: active ? 600 : 500,
                fontSize: adminTokens.textBase,
                fontFamily: adminTokens.fontSans,
                transition: 'background .1s, color .1s',
                '&:hover': {
                    bgcolor: adminTokens.gray100,
                    color: adminTokens.navy800,
                    '& .feed-kebab': { opacity: 1 },
                },
                '& .feed-kebab': {
                    opacity: active || anchor ? 1 : 0,
                },
            }}
        >
            <Typography
                noWrap
                sx={{
                    fontSize: 'inherit',
                    fontWeight: 'inherit',
                    color: 'inherit',
                    flex: 1,
                    minWidth: 0,
                }}
            >
                {label}
            </Typography>
            <IconButton
                className="feed-kebab"
                size="small"
                aria-label={`${label} actions`}
                onClick={(e) => {
                    e.stopPropagation();
                    setAnchor(e.currentTarget);
                }}
                sx={{
                    width: 28,
                    height: 28,
                    borderRadius: adminTokens.radius,
                    color: adminTokens.gray500,
                    '&:hover': { bgcolor: adminTokens.gray200, color: adminTokens.navy800 },
                }}
            >
                <PhosphorIcon name="dots-three" weight="bold" size={16} color={adminTokens.gray500} />
            </IconButton>
            <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => setAnchor(null)}
                onClick={(e) => e.stopPropagation()}
                PaperProps={{
                    sx: {
                        border: adminTokens.borderMd,
                        borderRadius: adminTokens.radiusMd,
                        boxShadow: adminTokens.shadowDropdown,
                        minWidth: 220,
                        p: 0.5,
                    },
                }}
            >
                {onDuplicate && (
                    <MenuItem
                        onClick={() => { onDuplicate(); setAnchor(null); }}
                        sx={{
                            fontSize: adminTokens.textBase,
                            fontWeight: 500,
                            height: 36,
                            borderRadius: adminTokens.radius,
                            gap: 1.5,
                            color: adminTokens.navy800,
                        }}
                    >
                        <PhosphorIcon name="copy" size={16} color={adminTokens.gray500} />
                        Duplicate feed
                    </MenuItem>
                )}
                {onDuplicate && <Divider sx={{ my: 0.5, borderColor: adminTokens.gray200 }} />}
                <MenuItem
                    onClick={() => { onDelete(); setAnchor(null); }}
                    sx={{
                        fontSize: adminTokens.textBase,
                        fontWeight: 500,
                        height: 36,
                        borderRadius: adminTokens.radius,
                        gap: 1.5,
                        color: adminTokens.errorSolid,
                        '&:hover': { bgcolor: adminTokens.errorBg },
                    }}
                >
                    <PhosphorIcon name="trash" weight="fill" size={16} color={adminTokens.errorSolid} />
                    Delete feed
                </MenuItem>
            </Menu>
        </Box>
    );
};

export const AddFeedButton = ({ onClick }: { onClick: () => void }) => (
    <SecondaryButton fullWidth onClick={onClick} sx={{ mt: 0.5 }}>
        <PhosphorIcon name="plus" size={14} color={adminTokens.navy800} />
        Add feed
    </SecondaryButton>
);
